import React, { useState, useRef } from 'react';
import { X, Upload, Download, Loader, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';

/**
 * Shared Import modal for CRM list pages (Leads, Deals, Accounts, Contacts).
 * Uploads a CSV/Excel file to `importEndpoint` and offers a matching sample
 * template download so users know what columns are expected.
 */
export default function ImportModal({ open, onClose, title, importEndpoint, sampleHeaders, sampleRow, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const downloadTemplate = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [sampleHeaders, sampleRow || sampleHeaders.map(() => '')];
    const csv = rows.map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    let imported = false;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(importEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const count = res.data.count ?? 0;
      const duplicatesCount = res.data.duplicatesCount || 0;
      setResult({
        success: true,
        count,
        duplicatesCount,
        skipped: res.data.skipped || [],
        errors: res.data.errors || [],
      });
      imported = true;
      // The rest of the app confirms every create/update/delete with an
      // alertService popup — import silently relied on the inline banner
      // below, which reads as "nothing happened" since it's easy to miss.
      alertService.success(
        'Import Complete',
        `Imported ${count} record${count === 1 ? '' : 's'}.${duplicatesCount > 0 ? ` Skipped ${duplicatesCount} duplicate${duplicatesCount === 1 ? '' : 's'}.` : ''}`
      );
      // Auto-close after a short delay so the success toast is visible.
      // Without this the modal stays open and users think "nothing happened".
      setTimeout(() => handleClose(), 2000);
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.message || 'Import failed' });
    } finally {
      setUploading(false);
    }
    // Runs after the try/finally above (not inside the try) so a throw here
    // can't overwrite the success result we already set with an error state.
    if (imported && onImported) {
      try { onImported(); } catch { /* list refresh failing shouldn't hide the import result */ }
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">{title}</h3>
          <button onClick={handleClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          <button
            type="button"
            onClick={downloadTemplate}
            className="w-full h-10 border border-dashed border-[#D8DDE6] rounded text-[12px] font-bold text-[#0176D3] hover:bg-[#F3F2F2] transition-all flex items-center justify-center gap-2"
          >
            <Download size={14} /> Download Sample Template (CSV)
          </button>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">CSV or Excel File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="w-full text-[13px] text-[#706E6B] file:mr-4 file:h-9 file:px-4 file:rounded file:border-0 file:bg-[#0176D3] file:text-white file:text-[12px] file:font-bold file:cursor-pointer hover:file:bg-[#014486]"
            />
            <p className="text-[11px] text-[#706E6B]">Rows matching an existing record (by email/phone) are skipped, not duplicated.</p>
          </div>

          {result && result.success && (
            <div className="space-y-2">
              <div className="p-3 rounded bg-[#EBF3ED] border border-[#2E844A]/30 text-[12px] text-[#2E844A] font-bold flex items-start gap-2">
                <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                <span>
                  Imported {result.count} record{result.count === 1 ? '' : 's'}.
                  {result.duplicatesCount > 0 && ` Skipped ${result.duplicatesCount} duplicate${result.duplicatesCount === 1 ? '' : 's'} already in your CRM.`}
                  {result.skipped?.length > 0 && ` Skipped ${result.skipped.length} empty row${result.skipped.length === 1 ? '' : 's'}.`}
                </span>
              </div>
              {result.errors?.length > 0 && (
                <div className="p-3 rounded bg-[#FDE7E9] border border-[#BA0517]/30 text-[12px] text-[#BA0517] space-y-1 max-h-40 overflow-y-auto">
                  <div className="font-bold flex items-center gap-2">
                    <AlertTriangle size={16} className="shrink-0" />
                    {result.errors.length} row{result.errors.length === 1 ? '' : 's'} failed:
                  </div>
                  {result.errors.map((e, i) => (
                    <div key={i} className="pl-6">Row {e.row}: {e.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {result && !result.success && (
            <div className="p-3 rounded bg-[#FDE7E9] border border-[#BA0517]/30 text-[12px] text-[#BA0517] font-bold flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{result.message}</span>
            </div>
          )}

          <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
            <button type="button" onClick={handleClose} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">
              {result?.success ? 'Done' : 'Cancel'}
            </button>
            {!(result && result.success) && (
              <button
                type="button"
                onClick={handleUpload}
                disabled={!file || uploading}
                className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? 'Uploading…' : 'Upload & Import'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
